import { BN, Program } from "@coral-xyz/anchor";
import { PublicKey, SYSVAR_RENT_PUBKEY, Transaction, SystemProgram } from "@solana/web3.js";
import { Buffer } from "buffer";
import { getAssociatedTokenAddress, ASSOCIATED_TOKEN_PROGRAM_ID, TOKEN_PROGRAM_ID } from "@solana/spl-token";
import nvaultIdl from '../web3/idl/stars_nvault.json';
import { getEnvironment } from "./helper";
import * as CONSTANTS from "../constants/constants";

const PROGRAM_NAME = "stars_nvault";
const devProgramId = 'FNqRnmWMqL5FoQa3M315QJGemSDHJP4PyHRQMDTgPJeW';
const prodProgramId = devProgramId;

const YEAR_IN_SECONDS = 31536000;
export const TOKEN_DECIMALS = 6;
export const DECIMAL_MULTIPLIER = 10 ** TOKEN_DECIMALS;

export const getVaultProgramData = provider => {
  if (!provider) throw new Error("Provider is required");
  const idl = nvaultIdl;
  try {
    const vaultProgram = new Program(idl, provider);
    return {
      program: vaultProgram,
      programName: PROGRAM_NAME,
      programId: new PublicKey(getEnvironment() === CONSTANTS.PRODUCTION ? prodProgramId : devProgramId)
    };
  } catch (error) {
    console.error("Error creating program:", error);
    throw error;
  }
};

export const getVaultState = async ({ provider }) => {
  const { program, programId } = getVaultProgramData(provider);
  const [statePda] = await PublicKey.findProgramAddressSync([Buffer.from("state")], programId);
  return program.account.vaultStateAccount.fetch(statePda);
};

export const checkIfUserHasAccount = async ({ provider }) => {
    const { program, programId } = getVaultProgramData(provider);
    const [userAccountPda] = await PublicKey.findProgramAddressSync(
        [Buffer.from("user_account"), provider.wallet.publicKey.toBuffer()],
        programId
    );
    return await provider.connection.getBalance(userAccountPda);
};

export const getUserAccount = async ({ provider }) => {
  const { program, programId } = getVaultProgramData(provider);
  const [userAccountPda] = await PublicKey.findProgramAddressSync(
    [Buffer.from("user_account"), provider.wallet.publicKey.toBuffer()],
    programId
  );
  return program.account.userAccount.fetch(userAccountPda);
};

export const getUserVaults = async ({ provider }) => {
  const { program, programId } = getVaultProgramData(provider);
  const [userAccountPda] = await PublicKey.findProgramAddressSync(
    [Buffer.from("user_account"), provider.wallet.publicKey.toBuffer()],
    programId
  );
  const userAccount = await program.account.userAccount.fetch(userAccountPda);
  const vaults = [];
  for (let i = 0; i < userAccount.totalVaults.toNumber(); i++) {
    const idxBuf = Buffer.from(new BN(i).toArray("le", 8));
    const [vaultPda] = await PublicKey.findProgramAddressSync(
      [Buffer.from("user_vault"), provider.wallet.publicKey.toBuffer(), idxBuf],
      programId
    );
    vaults.push({ publicKey: vaultPda, account: await program.account.userVault.fetch(vaultPda) });
  }
  return vaults;
};

export const createUser = async ({ provider, connection }) => {
  const { program, programId } = getVaultProgramData(provider);
  const [userAccountPda] = await PublicKey.findProgramAddressSync(
    [Buffer.from("user_account"), provider.wallet.publicKey.toBuffer()],
    programId
  );
  const signature = await program.methods
    .createUser()
    .accounts({
      user: provider.wallet.publicKey,
      userAccount: userAccountPda,
      systemProgram: SystemProgram.programId
    })
    .rpc();
  return connection.confirmTransaction(signature, "processed");
};

export const stakeStars = async ({ provider, connection, amount, lockOption }) => {
  const { program, programId } = getVaultProgramData(provider);
  const [statePda] = await PublicKey.findProgramAddressSync([Buffer.from("state")], programId);
  const vaultState = await program.account.vaultStateAccount.fetch(statePda);
  const programNameBytes = Buffer.from(vaultState.programName);
  const [vaultStarsPda] = await PublicKey.findProgramAddressSync(
    [programNameBytes, Buffer.from("vault_stars")],
    programId
  );
  const [userAccountPda] = await PublicKey.findProgramAddressSync(
    [Buffer.from("user_account"), provider.wallet.publicKey.toBuffer()],
    programId
  );
  const userAccount = await program.account.userAccount.fetch(userAccountPda);
  const idx = userAccount.totalVaults.toNumber();
  const idxBuf = Buffer.from(new BN(idx).toArray("le", 8));
  const [userVaultPda] = await PublicKey.findProgramAddressSync(
    [Buffer.from("user_vault"), provider.wallet.publicKey.toBuffer(), idxBuf],
    programId
  );
  const userStarsAccount = await getAssociatedTokenAddress(
    new PublicKey(vaultState.starsMint),
    provider.wallet.publicKey
  );
  const amountBn = new BN(amount);
  await program.methods
    .stakeStars(amountBn, lockOption)
    .accounts({
      state: statePda,
      userStars: userStarsAccount,
      vaultStars: vaultStarsPda,
      userAccount: userAccountPda,
      userVault: userVaultPda,
      user: provider.wallet.publicKey,
      tokenProgram: TOKEN_PROGRAM_ID,
      systemProgram: SystemProgram.programId
    })
    .rpc();
  //return connection.confirmTransaction(signature, "processed");
};

export const claimRewards = async ({ provider, connection, vaultPda, finalWithdrawal }) => {
  const { program, programId } = getVaultProgramData(provider);
  const [statePda] = await PublicKey.findProgramAddressSync([Buffer.from("state")], programId);
  const vaultState = await program.account.vaultStateAccount.fetch(statePda);
  const programNameBytes = Buffer.from(vaultState.programName);
  const [vaultStarsPda] = await PublicKey.findProgramAddressSync(
    [programNameBytes, Buffer.from("vault_stars")],
    programId
  );
  const userStarsAccount = await getAssociatedTokenAddress(
    new PublicKey(vaultState.starsMint),
    provider.wallet.publicKey
  );

  const signature = await program.methods
    .claimRewards(false)
    .accounts({
      state: statePda,
      userVault: vaultPda,
      vaultStars: vaultStarsPda,
      userStars: userStarsAccount,
      user: provider.wallet.publicKey,
      tokenProgram: TOKEN_PROGRAM_ID,
      systemProgram: SystemProgram.programId
    })
    .rpc();
  return connection.confirmTransaction(signature, "processed");
};

export const closeVault = async ({ provider, connection, vaultPda }) => {
  const { program, programId } = getVaultProgramData(provider);
  const [statePda] = await PublicKey.findProgramAddressSync([Buffer.from("state")], programId);
  const vaultState = await program.account.vaultStateAccount.fetch(statePda);
  const programNameBytes = Buffer.from(vaultState.programName);
  const [vaultStarsPda] = await PublicKey.findProgramAddressSync(
    [programNameBytes, Buffer.from("vault_stars")],
    programId
  );
  const userStarsAccount = await getAssociatedTokenAddress(
    new PublicKey(vaultState.starsMint),
    provider.wallet.publicKey
  );
  const signature = await program.methods
    .closeVault()
    .accounts({
      state: statePda,
      userVault: vaultPda,
      vaultStars: vaultStarsPda,
      userStars: userStarsAccount,
      user: provider.wallet.publicKey,
      tokenProgram: TOKEN_PROGRAM_ID
    })
    .rpc();
  return connection.confirmTransaction(signature, "processed");
};

export const convertHydrazine = async ({ provider, connection, amount }) => {
  console.log(amount)
  const { program, programId } = getVaultProgramData(provider);
  const [statePda] = await PublicKey.findProgramAddressSync([Buffer.from("state")], programId);
  const vaultState = await program.account.vaultStateAccount.fetch(statePda);
  const programNameBytes = Buffer.from(vaultState.programName);
  const [vaultStarsPda] = await PublicKey.findProgramAddressSync(
    [programNameBytes, Buffer.from("vault_stars")],
    programId
  );
  const userHydrazineAccount = await getAssociatedTokenAddress(
    new PublicKey(vaultState.hydrazineMint),
    provider.wallet.publicKey
  );
  const userStarsAccount = await getAssociatedTokenAddress(
    new PublicKey(vaultState.starsMint),
    provider.wallet.publicKey
  );
  const amountBn = new BN(amount);
  const signature = await program.methods
    .convertHydrazine(amountBn)
    .accounts({
      state: statePda,
      userHydrazine: userHydrazineAccount,
      userStars: userStarsAccount,
      hydrazineMint: vaultState.hydrazineMint,
      starsMint: vaultState.starsMint,
      vaultStars: vaultStarsPda,
      user: provider.wallet.publicKey,
      tokenProgram: TOKEN_PROGRAM_ID
    })
    .rpc();
  return connection.confirmTransaction(signature, "processed");
};

export const getTotalStakedStars = async ({ provider }) => {
  const vaults = await getUserVaults({ provider });
  let total = new BN(0);

  for (const { account } of vaults) {
    total = total.add(account.lockedAmount);
  }

  const totalStakedFormatted = total.toNumber() / 1_000_000;
  return totalStakedFormatted;
};

export const getTotalStarsStakedInProgram = async ({ provider }) => {
  const { program, programId } = getVaultProgramData(provider);
  const [statePda] = await PublicKey.findProgramAddressSync([Buffer.from("state")], programId);
  const vaultState = await program.account.vaultStateAccount.fetch(statePda);
  const programNameBytes = Buffer.from(vaultState.programName);
  const [vaultStarsPda] = await PublicKey.findProgramAddressSync(
    [programNameBytes, Buffer.from("vault_stars")],
    programId
  );

  const vaultStarsAccountInfo = await provider.connection.getTokenAccountBalance(vaultStarsPda);
  const amountUi = vaultStarsAccountInfo.value.uiAmount;

  return amountUi;
};


export const calculateAccruedRewards = (lockedAmount, apr, startTime, currentTime, lastClaimTime) => {
  const periodStart = Math.max(startTime, lastClaimTime);
  const timeElapsed = currentTime - periodStart;

  const yearFraction = timeElapsed / YEAR_IN_SECONDS;
  const aprDecimal = apr / 10000;
  const rewards = lockedAmount * aprDecimal * yearFraction;

  return rewards;
}

export const updateHydrazineExchangeRates = async ({ provider, hydrazineAprs, exchangeRates }) => {
    try {
        const { program, programId } = getVaultProgramData(provider);

        const [statePda] = PublicKey.findProgramAddressSync(
            [Buffer.from('state')],
            programId
        );

        // Read parameters
        const hydrazineYearApr        = new BN(Number(hydrazineAprs.yearLockApr) * 1000000);
        const hydrazineHalfYearApr    = new BN(Number(hydrazineAprs.halfYearApr) * 1000000);  // bps
        const hydrazineQuarterYearApr = new BN(Number(hydrazineAprs.quarterYearApr) * 1000000);

        const xrateYear        = new BN(Number(exchangeRates.year) * 10000);
        const xrateHalfYear    = new BN(Number(exchangeRates.halfYear) * 10000);
        const xrateQuarterYear = new BN(Number(exchangeRates.quarterYear) * 10000);
        const exchangeRate     = new BN(Number(exchangeRates.exchangeRate) * 10000);

        console.log(xrateYear)

        const tenThousand = new BN(10_000);
        for (const [name, val] of [
            ['HYDRAZINE_APR_YEAR', hydrazineYearApr],
            ['HYDRAZINE_APR_HALF_YEAR', hydrazineHalfYearApr],
            ['HYDRAZINE_APR_QUARTER_YEAR', hydrazineQuarterYearApr],
        ]) {
            if (val.gt(tenThousand)) {
                console.error(`Env ${name} should be in basis points (0..10000). Got ${val.toString()}`);
                process.exit(1);
            }
        }

        console.log('\n› update_hydrazine_rates');
        console.table({
            HYDRAZINE_APR_YEAR:         hydrazineYearApr.toString(),
            HYDRAZINE_APR_HALF_YEAR:    hydrazineHalfYearApr.toString(),
            HYDRAZINE_APR_QUARTER_YEAR: hydrazineQuarterYearApr.toString(),
            XRATE_YEAR:                 xrateYear.toString(),
            XRATE_HALF_YEAR:            xrateHalfYear.toString(),
            XRATE_QUARTER_YEAR:         xrateQuarterYear.toString(),
        });

        if (exchangeRates?.exchangeRate) {
            await program.methods
                .updateExchangeRate(exchangeRate)
                .accounts({
                    state:     statePda,
                    authority: provider.wallet.publicKey,
                })
                .rpc();
        }


        // Send the transaction
        return await program.methods
            .updateHydrazineRates(
                hydrazineYearApr,
                hydrazineHalfYearApr,
                hydrazineQuarterYearApr,
                xrateYear,
                xrateHalfYear,
                xrateQuarterYear,
            )
            .accounts({
                state:     statePda,
                authority: provider.wallet.publicKey,
            })
            .rpc();
    } catch (error) {
        console.error('Failed to update hydrazine exchange rates:', error);
    }


}

export const updateAprRates = async ({ provider, aprs }) => {
    try {
        const { program, programId } = getVaultProgramData(provider);

        const [statePda] = PublicKey.findProgramAddressSync(
            [Buffer.from('state')],
            programId
        );

        // Read parameters
        const yearLockApr        = new BN(Number(aprs.yearLockApr));
        const halfYearApr        = new BN(Number(aprs.halfYearApr));  // bps
        const quarterYearApr     = new BN(Number(aprs.quarterYearApr));


        await program.methods
            .updateRates(yearLockApr, halfYearApr, quarterYearApr)
            .accounts({
                state:     statePda,
                authority: provider.wallet.publicKey,
            })
            .rpc();

    } catch (error) {
        console.error('Failed to update APR rates:', error);
    }
}